import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, ChartFrame, InfoHint, toast } from '../../components/ui';
import { cn } from '../../lib/cn';
import { formatMoney, formatMoneyCompact, formatMoneySigned } from '../../lib/format';
import { useSetPlan, type CashflowReport } from '../cashflow/api';

const CASH_COLOR = '#142033'; // navy — the projected balance
const ACTUAL_COLOR = '#2563eb'; // accent — what was actually in the bank
const CASH_GOAL_COLOR = '#e61515'; // brand red — THE reference line
const REVENUE_GOAL_COLOR = '#9898A5'; // grey dashed — the flat revenue target
const REVENUE_COLOR = '#93c5fd';

/**
 * Two charts, because the two questions live on different scales, and they are
 * exported SEPARATELY because the page places them in different rows.
 *
 * A cash BALANCE (~$250k) and a per-period revenue GOAL (~$82k) plotted on one
 * axis would flatten whichever one you actually came to read. So: the cash
 * trajectory answers "what will cash be on date X", and revenue against the
 * flat goal line answers "are we billing enough".
 *
 * ⚠ **TWO GOALS ON ONE SCREEN, AND THEY ARE NOT THE SAME NUMBER.**
 *   · The **red rising line** on the cash chart is a **cash-balance target** —
 *     the bank balance Jesse's own chart climbs $10,000 a fortnight toward.
 *   · The **grey dashed line** on the revenue chart is the **annual revenue
 *     goal** ($1,957,296) spread across 24 periods.
 * Each chart names its own in words, in its legend and in its tooltip. An
 * unlabelled "goal" on a money screen is exactly how two targets become one
 * wrong one.
 */

/**
 * **ROUND AXIS TICKS.** Recharts' default is `max ÷ 4`, which gave the revenue
 * chart $0 / $55K / $110K / $165K / $220K and Northline's $0 / $3.5K / $7K /
 * $10.5K / $14K. Financial charts step in round money — $50K, $100K — because
 * a reader estimates a bar by counting gridlines, and counting in 55s is work.
 *
 * Steps are 1 / 2 / 2.5 / 5 / 10 × a power of ten, the same ladder every
 * charting library's "nice numbers" routine uses, and the domain is the first
 * and last tick so the plot ends ON a labelled line rather than between two.
 */
export function niceTicks(min: number, max: number, target = 5): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
  if (max <= min) return [min, min + 1];
  const raw = (max - min) / Math.max(1, target - 1);
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const ladder = [1, 2, 2.5, 5, 10].map((m) => m * magnitude);
  let step = ladder.find((candidate) => candidate >= raw) ?? 10 * magnitude;
  // Rounding the step UP alone leaves a chart mostly empty — $215,797 of
  // revenue against a $100K step draws an axis to $300K and the tallest bar
  // reaches 72% of the plot. Drop to the next rung down when it still keeps the
  // gridlines countable, which is what turns $0/$100K/$200K/$300K into
  // $0/$50K/…/$250K, the steps a financial chart is expected to use.
  const finer = ladder.filter((candidate) => candidate < step).pop();
  if (finer && (max - min) / finer <= 8) step = finer;
  const start = Math.floor(min / step) * step;
  const ticks: number[] = [];
  // `+ step / 1000` absorbs float drift so a max landing exactly on a tick
  // still gets that tick rather than one more gridline above the data.
  for (let value = start; value <= max + step / 1000; value += step) {
    ticks.push(Math.round(value * 100) / 100);
    if (ticks.length > 24) break;
  }
  if (ticks.length < 2) ticks.push(start + step);
  return ticks;
}

interface ChartRow {
  key: string;
  label: string;
  month: string;
  ending: number;
  actual: number | null;
  cashGoal: number;
  revenue: number;
  /** Periods that have finished — money that actually came in. */
  revenueBooked: number;
  /** This period and everything after — money we are forecasting. */
  revenueForecast: number;
  booked: boolean;
  goal: number;
  potential: number;
}

function useChartRows(report: CashflowReport): ChartRow[] {
  return useMemo(
    () =>
      report.periods.map((p) => ({
        key: p.key,
        label: p.label,
        month: p.half === 1 ? p.monthLabel : '',
        ending: p.endingCash,
        actual: p.actualCash,
        cashGoal: p.cashGoal,
        revenue: p.revenue,
        // ⚠ **BOOKED AND FORECAST ARE NEVER THE SAME MARK.** The cash chart
        // beside this one distinguishes "Recorded actual" from "Projected
        // ending"; this one drew eight months of banked revenue and four months
        // of guesswork in one identical solid blue. Split at the period
        // boundary — a period still running is forecast, because its money is
        // not all in.
        revenueBooked: p.isPast ? p.revenue : 0,
        revenueForecast: p.isPast ? 0 : p.revenue,
        booked: p.isPast,
        goal: p.goal,
        potential: p.projectedPotential,
      })),
    [report.periods],
  );
}

/** The chart this screen exists for: the balance against the red target. */
export function CashBalanceChart({
  report,
  className,
  planHref,
}: {
  report: CashflowReport;
  className?: string;
  /** Where the goal is actually set — this chart's one call to action. */
  planHref: string;
}) {
  const rows = useChartRows(report);
  const setPlan = useSetPlan(report.year);
  /**
   * ⚠ **THE TARGET IS ALWAYS DRAWN. WHAT CHANGES IS HOW MUCH THE APP CLAIMS
   * FOR IT.**
   *
   * `/api/cashflow` always returns a full `periods[].cashGoal` series and the
   * Report tab always prints its two parameters in the plan strip. This chart
   * used to refuse to draw the series whenever `cashGoalSource === 'default'`,
   * so the same figures were "the 2026 plan" on one tab and "no target is set"
   * on another — and the app's headline feature, the rising cash line, was
   * invisible on the screen it exists for. The cost was the whole reading:
   * Solid Digital sits BELOW this target in 16 of 24 periods (worst: $212,450
   * against $364,340 in Jul 1–15) while the chart happily drew a hockey stick
   * to $522,830 with nothing to read it against.
   *
   * So: **confirmed** (`plan`) draws solid red and owns the axis like any other
   * series. **Unconfirmed** (`default` — this year's opening cash rising
   * $10,000 a period, proposed by us) draws DASHED, says it is a proposal, and
   * carries a one-click "Use this target" that writes those exact figures to
   * the cash plan. One click, on this screen, instead of a navigation and two
   * retypes of numbers already on screen.
   *
   * The original objection to drawing a default was real and is answered by the
   * AXIS RULE below, not by silence.
   */
  const goalConfirmed = report.plan.cashGoalSource === 'plan';
  const hasCashGoal = report.plan.cashGoalIncrement !== 0 || report.plan.cashGoalStart !== 0;
  const currency = report.currency;
  const money = (value: number) => formatMoney(value, { currency, whole: true });
  /** An overdraft must stay visible, so the floor is 0 *or* the low point —
   *  never a cropped axis that hides the month the balance goes under. */
  const dataLow = Math.min(0, ...rows.map((r) => r.ending), ...rows.map((r) => r.actual ?? 0));
  const dataHigh = Math.max(
    0,
    ...rows.map((r) => r.ending),
    ...rows.flatMap((r) => (r.actual === null ? [] : [r.actual])),
  );
  const goalHigh = hasCashGoal ? Math.max(...rows.map((r) => r.cashGoal)) : dataHigh;
  const goalLow = hasCashGoal ? Math.min(...rows.map((r) => r.cashGoal)) : dataLow;
  /**
   * ⚠ **AN UNCONFIRMED TARGET MAY NOT COMPRESS THE REAL DATA.** A confirmed
   * goal is a fact about the business and owns the axis. A PROPOSAL does not:
   * on Northline Creative the proposed line climbs $34,300 → $264,300 against a
   * real balance that never passes $44,945, and letting it set the top would
   * squash the actual cash into the bottom sixth of the plot — a placeholder
   * that makes the data harder to read has negative value.
   *
   * So a proposal may lift the ceiling by at most HALF the data's own span, and
   * beyond that it clips (`allowDataOverflow` gives Recharts the clip path).
   * Solid Digital's proposal tops out at $474,340 against data reaching
   * $582,730, so it is entirely inside the plot and nothing is clipped; only a
   * wildly out-of-scale proposal is ever cut, and the subtitle says so.
   */
  const proposalCeiling = dataHigh + Math.max(dataHigh - dataLow, 1) * 0.5;
  const highest = goalConfirmed
    ? Math.max(dataHigh, goalHigh)
    : Math.max(dataHigh, Math.min(goalHigh, proposalCeiling));
  const lowest = goalConfirmed ? Math.min(dataLow, goalLow) : dataLow;
  const ticks = niceTicks(lowest, highest);
  const goalClipped = hasCashGoal && goalHigh > (ticks[ticks.length - 1] ?? highest);

  /**
   * **THE READING THIS CHART EXISTS FOR** — stated, not left to be eyeballed
   * off two lines. Actual where it is recorded, projection where it is not:
   * the same reference the tooltip's variance row uses.
   */
  const current =
    report.periods.find((p) => p.key === report.currentPeriodKey) ??
    report.periods[report.periods.length - 1];
  const currentRef = current ? (current.actualCash ?? current.endingCash) : 0;
  const currentGap = current ? currentRef - current.cashGoal : 0;
  const belowCount = report.periods.filter(
    (p) => (p.actualCash ?? p.endingCash) < p.cashGoal,
  ).length;

  const confirm = () => {
    setPlan.mutate(
      {
        cashGoalStart: report.plan.cashGoalStart,
        cashGoalIncrement: report.plan.cashGoalIncrement,
      },
      {
        onSuccess: () =>
          toast(
            `Cash target set for ${report.year}: ${money(report.plan.cashGoalStart)} rising ${formatMoneySigned(report.plan.cashGoalIncrement, { currency, whole: true })} a period.`,
            { kind: 'success' },
          ),
        onError: (error: unknown) =>
          toast(error instanceof Error ? error.message : 'Could not save the cash target.', {
            kind: 'error',
          }),
      },
    );
  };

  return (
    /* The shared `Card`, not a hand-rolled shell: this was the app's second
       card style (same border, same shadow, its own `flex h-full flex-col`),
       which is precisely how a design-system rule stops reaching a screen.
       `fill="md"` is the CEILING from the BRIEF — it grows into the row the
       Needs-attention column defines and then stops, so a twenty-late-invoice
       month pushes that column down without dragging the graph with it. */
    <Card fill="md" className={cn('p-4 sm:p-5', className)}>
        <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-heading-4 text-secondary">
            {/* The title is the way IN (Jesse, 2026-08-20): this chart is the
                Report grid's summary, so its name links to the screen that
                owns the numbers — the same row-label-is-a-link rule the grid
                itself follows. */}
            <Link
              to="/budget-forecast?tab=report"
              className="rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
            >
              Cash balance by period
            </Link>
            {/* ⚠ **THE PARAGRAPH THAT USED TO SIT UNDER THIS TITLE IS IN HERE.**
                Jesse, 2026-08-13: *"remove the 'Where the balance…'"*. The
                legend already names the red line and says it is not confirmed;
                the figures row below already prints the gap. What was left was
                a lesson about what a chart of a balance is. It is not deleted —
                it is one click away, for the reader who wants it. */}
            <InfoHint label="Cash balance by period" className="ml-1.5" size="md" width={320}>
              <p>
                The projected ending balance of each half-month period, with recorded actuals
                drawn over it.
              </p>
              {hasCashGoal && (
                <p className="mt-1.5">
                  The{' '}
                  <span className="font-semibold text-primary-600">
                    {goalConfirmed ? 'red line' : 'dashed red line'}
                  </span>{' '}
                  is a <strong>bank-balance</strong> target — {money(report.plan.cashGoalStart)}{' '}
                  rising{' '}
                  <span className="num">
                    {formatMoneySigned(report.plan.cashGoalIncrement, { currency, whole: true })}
                  </span>{' '}
                  a period.{' '}
                  {goalConfirmed
                    ? ''
                    : 'It is this year’s opening cash and nobody has confirmed it — “Use this target” saves it as the plan. '}
                  It is <strong>not the revenue goal</strong>, which is the grey dashed line on the
                  revenue chart.
                  {goalClipped ? ' It climbs off the top of this plot.' : ''}
                </p>
              )}
            </InfoHint>
          </h3>
          <Legend
            items={[
              { color: CASH_COLOR, label: 'Projected ending' },
              { color: ACTUAL_COLOR, label: 'Recorded actual' },
              ...(hasCashGoal
                ? [
                    goalConfirmed
                      ? { color: CASH_GOAL_COLOR, label: 'Cash target', bold: true }
                      : {
                          color: CASH_GOAL_COLOR,
                          label: 'Cash target · not confirmed',
                          dashed: true,
                        },
                  ]
                : []),
            ]}
          />
        </div>
        {/* No target, no line — and that is an ACTION, not an explanation, so it
            stays on screen. One line, not a paragraph. */}
        {!hasCashGoal && (
          <p className="mb-3 text-caption text-grey-500">
            No cash target set for {report.year}.{' '}
            <Link to={planHref} className="font-semibold text-accent hover:underline">
              Set one
            </Link>
          </p>
        )}
        {/*
          ⚠ **THE ANSWER, AS FIGURES.** "Are we on track on cash?" is the
          question Float and Pulse exist to answer, and this chart was silent on
          it — the gap between the two lines was left to be eyeballed. It is one
          row of data, not a paragraph.

          The **Use this target** button is the whole escape hatch: it PUTs the
          two figures already on screen to this year's cash plan, so the line
          goes solid without leaving the dashboard. Before, the CTA navigated to
          `/budget-forecast?tab=report` and dropped focus on `<body>`, where the
          owner had to retype the app's own numbers over themselves to make the
          line appear.
        */}
        {hasCashGoal && current && (
          <p className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-caption text-grey-500">
            <span>
              {current.label}:{' '}
              <span className="num font-semibold text-secondary">{money(currentRef)}</span>
              {current.actualCash === null ? ' projected' : ' recorded'} against a{' '}
              <span className="num">{money(current.cashGoal)}</span> target —{' '}
              <span
                className={cn(
                  'num font-semibold',
                  currentGap < 0 ? 'text-primary-600' : 'text-success',
                )}
              >
                {money(Math.abs(currentGap))} {currentGap < 0 ? 'below' : 'above'}
              </span>
              .
            </span>
            <span className="text-grey-400">
              Below target in {belowCount} of {report.periods.length} periods.
            </span>
            {!goalConfirmed && (
              <span className="ml-auto inline-flex items-center gap-2">
                <button
                  type="button"
                  onClick={confirm}
                  disabled={setPlan.isPending}
                  className="rounded-md border border-primary-200 bg-primary-50 px-2 py-0.5 text-caption font-semibold text-primary-600 transition-colors hover:bg-primary-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-50"
                >
                  {setPlan.isPending ? 'Saving…' : 'Use this target'}
                </button>
                <Link
                  to={`${planHref}&focus=cash-goal`}
                  className="font-semibold text-accent hover:underline"
                >
                  Set your own
                </Link>
              </span>
            )}
          </p>
        )}
        {/* The headline chart of the screen, and the tallest thing on it: the
            gap between the black line and the red one is what people come
            here to read.

            ⚠ **Fills, bounded.** `ChartFrame` is `flex-1 min-h-0`, so the plot
            area takes the height the card was given (BRIEF: *"can we have the
            graph fill in the height that is given to it?"*); `min="lg"` keeps
            it readable when the row is short. The CEILING is the CARD's
            `fill`, never a height here — one bound per card, so the plot and
            its chrome cannot disagree about where the card ends. */}
        <ChartFrame min="lg">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: -8 }}>
              <CartesianGrid stroke="#f2f2f2" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 10, fill: '#9898A5' }}
                axisLine={{ stroke: '#eaeaea' }}
                tickLine={false}
                interval={0}
              />
              <YAxis
                // From $0 (or the low point — an overdraft must stay visible),
                // and stepping in ROUND money: the shape of the gap is the
                // point of this chart, and a floating or oddly-stepped baseline
                // makes it guesswork.
                domain={[ticks[0] ?? 0, ticks[ticks.length - 1] ?? 'auto']}
                ticks={ticks}
                tick={{ fontSize: 10, fill: '#9898A5' }}
                axisLine={false}
                tickLine={false}
                width={62}
                tickFormatter={(v: number) => formatMoneyCompact(v, currency)}
              />
              {lowest < 0 && <ReferenceLine y={0} stroke="#c8c8d0" strokeWidth={1} />}
              <Tooltip content={<CashTooltip currency={currency} hasGoal={hasCashGoal} />} />
              <Area
                type="monotone"
                dataKey="ending"
                stroke={CASH_COLOR}
                strokeWidth={2}
                fill={CASH_COLOR}
                fillOpacity={0.06}
                dot={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="actual"
                stroke={ACTUAL_COLOR}
                strokeWidth={2}
                dot={{ r: 2.5, fill: ACTUAL_COLOR, strokeWidth: 0 }}
                connectNulls
                isAnimationActive={false}
              />
              {/* Straight, rising, red, and drawn last so nothing overlaps it —
                  it is the reference every other line is read against.

                  ⚠ **A MACHINE GUESS MAY NOT BE DRAWN WITH THE AUTHORITY OF A
                  TARGET.** The legend said "not confirmed · dashed" and the
                  prose said "the DASHED red line", while the line itself was
                  the same solid 2.25px rule a confirmed goal gets — so an
                  opening-cash-plus-$10k guess wore the exact weight of a number
                  somebody had actually set. Solid brand red is reserved for
                  `cashGoalSource: 'plan'`. */}
              {hasCashGoal && (
                <Line
                  type="linear"
                  dataKey="cashGoal"
                  stroke={CASH_GOAL_COLOR}
                  strokeWidth={goalConfirmed ? 2.25 : 1.75}
                  strokeDasharray={goalConfirmed ? undefined : '6 4'}
                  strokeOpacity={goalConfirmed ? 1 : 0.6}
                  dot={false}
                  isAnimationActive={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </ChartFrame>
    </Card>
  );
}

/** Money in per period, against the annual REVENUE goal ÷ 24. */
export function RevenueGoalChart({
  report,
  className,
}: {
  report: CashflowReport;
  className?: string;
}) {
  const rows = useChartRows(report);
  const hasRevenueGoal = report.plan.goal > 0;
  const currency = report.currency;
  const money = (value: number) => formatMoney(value, { currency, whole: true });
  /* ⚠ **THE SERVER'S FIGURES, NOT A SECOND SUM.** `revenuePace.booked` is
     computed from the same `isPast` cut these bars are drawn on, and the KPI
     tile above prints it as its headline. Re-summing here would coincide today
     and drift the first time either rule moves — and this chart's subtitle
     disagreeing with the tile three inches above it is precisely the bug this
     whole pass exists to close. */
  const pace = report.revenuePace;
  const bookedTotal = pace.booked;
  const forecastTotal = pace.projected - pace.booked;
  const behind = pace.gap < 0;
  const revenueTicks = niceTicks(
    0,
    Math.max(0, ...rows.map((r) => r.revenue), ...(hasRevenueGoal ? rows.map((r) => r.goal) : [])),
  );

  return (
      /* Same shell and same rule as the cash chart; a lower ceiling because the
         card beside it (Bonus status) is a fixed two-condition readout, not a
         list that can run long. */
      <Card fill="sm" className={cn('p-4 sm:p-5', className)}>
        <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-heading-4 text-secondary">Revenue vs goal</h3>
          <Legend
            items={[
              { color: REVENUE_COLOR, label: 'Collected' },
              { color: REVENUE_COLOR, label: 'Forecast', hatched: true },
              ...(hasRevenueGoal
                ? [{ color: REVENUE_GOAL_COLOR, label: 'Revenue goal', dashed: true }]
                : []),
            ]}
          />
        </div>
        <p className="mb-3 text-body-sm text-grey-500">
          <span className="font-semibold text-secondary">{money(bookedTotal)} collected</span>
          {hasRevenueGoal && pace.pct !== null ? (
            <>
              {' '}
              against {money(pace.goalToDate)} of goal to date —{' '}
              <span className={cn('font-semibold', behind ? 'text-primary-600' : 'text-success')}>
                {money(Math.abs(pace.gap))} {behind ? 'behind' : 'ahead'}
              </span>
              .
            </>
          ) : (
            '.'
          )}{' '}
          Hatched bars are {money(forecastTotal)} still to come, this period included.{' '}
          {hasRevenueGoal
            ? `The dashed line is the ${money(report.plan.goal)} annual goal ÷ 24.`
            : 'Set a revenue goal to draw the target line.'}{' '}
          {/* ⚠ WHICH REVENUE. Solid bars are cash BANKED. This used to be the
              odd one out on the screen — the Effective hourly rate card divided
              by INVOICED revenue and landed ~$98K higher on the same year — and
              since 2026-08-13 it is the rule instead: revenue means cash
              everywhere in this app. The clause stays because a bar chart of
              money still has to say whether the money arrived; billed-and-
              unpaid is on /invoices. */}
          Solid bars are cash collected, not invoiced — money counts when it arrives.{' '}
        </p>
        {/* Same fill rule as the cash chart above — see the note there. */}
        <ChartFrame min="sm">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: -8 }}>
              {/* The hatch IS the distinction — a financial chart may not draw
                  banked money and a projection with the same mark. */}
              <defs>
                <pattern
                  id="revenue-forecast-hatch"
                  patternUnits="userSpaceOnUse"
                  width={5}
                  height={5}
                  patternTransform="rotate(45)"
                >
                  <rect width={5} height={5} fill="#eff6ff" />
                  <line x1={0} y1={0} x2={0} y2={5} stroke={REVENUE_COLOR} strokeWidth={2.5} />
                </pattern>
              </defs>
              <CartesianGrid stroke="#f2f2f2" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 10, fill: '#9898A5' }}
                axisLine={{ stroke: '#eaeaea' }}
                tickLine={false}
                interval={0}
              />
              <YAxis
                domain={[0, revenueTicks[revenueTicks.length - 1] ?? 'auto']}
                ticks={revenueTicks}
                tick={{ fontSize: 10, fill: '#9898A5' }}
                axisLine={false}
                tickLine={false}
                width={54}
                tickFormatter={(v: number) => formatMoneyCompact(v, currency)}
              />
              <Tooltip content={<RevenueTooltip currency={currency} />} />
              <Bar
                dataKey="revenueBooked"
                stackId="revenue"
                fill={REVENUE_COLOR}
                radius={[2, 2, 0, 0]}
                isAnimationActive={false}
              />
              <Bar
                dataKey="revenueForecast"
                stackId="revenue"
                fill="url(#revenue-forecast-hatch)"
                stroke={REVENUE_COLOR}
                strokeWidth={0.75}
                radius={[2, 2, 0, 0]}
                isAnimationActive={false}
              />
              {hasRevenueGoal && (
                <Line
                  type="linear"
                  dataKey="goal"
                  stroke={REVENUE_GOAL_COLOR}
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  dot={false}
                  isAnimationActive={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </ChartFrame>
      </Card>
  );
}

function Legend({
  items,
}: {
  items: Array<{
    color: string;
    label: string;
    dashed?: boolean;
    bold?: boolean;
    /** Draws the swatch as the forecast hatch, so the key matches the mark. */
    hatched?: boolean;
  }>;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {items.map((item) => (
        <span
          key={item.label}
          className={
            item.bold
              ? 'inline-flex items-center gap-1.5 text-caption font-semibold text-secondary'
              : 'inline-flex items-center gap-1.5 text-caption text-grey-500'
          }
        >
          <span
            className="inline-block h-[3px] w-4 rounded-full"
            style={{
              backgroundColor: item.dashed || item.hatched ? 'transparent' : item.color,
              borderTop: item.dashed ? `2px dashed ${item.color}` : undefined,
              height: item.bold ? 4 : item.hatched ? 9 : undefined,
              borderRadius: item.hatched ? 2 : undefined,
              border: item.hatched ? `1px solid ${item.color}` : undefined,
              backgroundImage: item.hatched
                ? `repeating-linear-gradient(45deg, ${item.color} 0 2px, transparent 2px 5px)`
                : undefined,
            }}
            aria-hidden
          />
          {item.label}
        </span>
      ))}
    </div>
  );
}

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ payload: ChartRow }>;
  currency: string;
}

function CashTooltip({ active, payload, currency, hasGoal }: TooltipProps & { hasGoal?: boolean }) {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;
  // The gap to the goal is the whole point of the chart, so it is stated
  // rather than left for the reader to subtract two numbers by eye — but only
  // when there IS a goal. The tooltip used to report a variance against the
  // machine-invented default, which is a number about nothing.
  const reference = row.actual ?? row.ending;
  return (
    <TooltipShell title={row.label}>
      {/* A closed fortnight is not a projection — its ending is computed from
          what the invoices actually did (Jesse, 2026-08-21: "if the date is in
          the past it should just say ending not projected"). `booked` is the
          server's own isPast cut. */}
      <Row
        label={row.booked ? 'Ending' : 'Projected ending'}
        value={formatMoney(row.ending, { currency, whole: true, parens: true })}
      />
      {row.actual !== null && (
        <Row label="Recorded actual" value={formatMoney(row.actual, { currency, whole: true, parens: true })} />
      )}
      {hasGoal && (
        <>
          <Row
            label="Cash goal"
            value={formatMoney(row.cashGoal, { currency, whole: true })}
            tone="text-primary-600"
          />
          <Row
            label={
              row.actual !== null
                ? 'Actual vs goal'
                : row.booked
                  ? 'Ending vs goal'
                  : 'Projected vs goal'
            }
            value={formatMoneySigned(reference - row.cashGoal, { currency, whole: true })}
            tone={reference >= row.cashGoal ? 'text-success' : 'text-primary-600'}
          />
        </>
      )}
      {row.potential > 0 && (
        <Row
          label="Potential (not counted)"
          value={formatMoney(row.potential, { currency, whole: true })}
          muted
        />
      )}
    </TooltipShell>
  );
}

function RevenueTooltip({ active, payload, currency }: TooltipProps) {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;
  return (
    <TooltipShell title={row.label}>
      {/* Named, not just shaded: the reader must be able to tell banked money
          from a projection without decoding a fill. */}
      <Row
        label={row.booked ? 'Revenue · collected' : 'Revenue · forecast'}
        value={formatMoney(row.revenue, { currency, whole: true })}
      />
      {row.goal > 0 && (
        <Row label="Revenue goal" value={formatMoney(row.goal, { currency, whole: true })} muted />
      )}
    </TooltipShell>
  );
}

function TooltipShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="min-w-[13rem] rounded-lg border border-grey-200 bg-white px-3 py-2 shadow-elevated">
      <p className="text-caption font-semibold uppercase tracking-wider text-grey-500">{title}</p>
      <div className="mt-1 space-y-0.5">{children}</div>
    </div>
  );
}

function Row({
  label,
  value,
  muted,
  tone,
}: {
  label: string;
  value: string;
  muted?: boolean;
  tone?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-6 text-sm">
      <span className={muted ? 'text-grey-400' : 'text-grey-600'}>{label}</span>
      <span className={`num font-semibold ${tone ?? (muted ? 'text-grey-500' : 'text-secondary')}`}>
        {value}
      </span>
    </div>
  );
}
